import { IntegrationsApiClient } from '@webapp/core/services/IntegrationsAPIClient/IntegrationsAPIClient.service'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import type { SaveIntegrationSettingsApiPayload } from '@webapp/platform/integrations/data-access/api-models/IntegrationSettingApiModel'
import { mapIntegrationSettingApiModel } from '@webapp/platform/integrations/data-access/mappers/mapIntegrationSettingApiModel'
import type {
  IntegrationSettingConstructor,
  Integration,
} from '@webapp/platform/integrations/entities'

export interface IIntegrationSettingsRepository {
  getIntegrationSettings(
    integrationId: string
  ): Promise<IntegrationSettingConstructor[]>
  saveIntegrationSettings(
    integrationId: Integration['id'],
    settings: SaveIntegrationSettingsApiPayload
  ): Promise<IntegrationSettingConstructor[]>
  deleteIntegrationSetting(
    integrationId: Integration['id'],
    settingId: string
  ): Promise<void>
}

interface Dependency {
  httpClient: typeof IntegrationsApiClient
}

export class IntegrationSettingsRepository
  implements IIntegrationSettingsRepository
{
  constructor(private readonly deps: Dependency) {}

  async getIntegrationSettings(
    integrationId: Integration['id']
  ): Promise<IntegrationSettingConstructor[]> {
    const { data: settings } =
      await this.deps.httpClient.getIntegrationSettings({
        integration_id: integrationId,
      })

    return settings.map((setting) => mapIntegrationSettingApiModel(setting))
  }

  async saveIntegrationSettings(
    integrationId: Integration['id'],
    settingsPayload: SaveIntegrationSettingsApiPayload
  ): Promise<IntegrationSettingConstructor[]> {
    try {
      const { data: settings } =
        await this.deps.httpClient.saveIntegrationSettings(
          { integration_id: integrationId },
          settingsPayload
        )
      return settings.map((setting) => mapIntegrationSettingApiModel(setting))
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async deleteIntegrationSetting(
    integrationId: Integration['id'],
    settingId: string
  ): Promise<void> {
    try {
      await this.deps.httpClient.deleteIntegrationSetting({
        integration_id: integrationId,
        setting_id: settingId,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}

export const integrationSettingsRepository = new IntegrationSettingsRepository({
  httpClient: IntegrationsApiClient,
})
